import { useDebounceValue } from '@repo/hooks/useDebounceValue';
import { usePlatformShortcutLabel } from '@repo/hooks/usePlatformShortcutLabel';
import { cn } from '@repo/lib/cn';
import { CircleXIcon, SearchIcon } from 'lucide-react';
import { startTransition, useEffect, useEffectEvent, useRef, useState } from 'react';

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandLoading } from '@/components/ui/command';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group';
import { Kbd } from '@/components/ui/kbd';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import type { InputSearchProps, SearchGroup, SimpleSearchInputProps } from '.';

/**
 * `SearchInput` — a generic, grouped, command-style search input with a popover result list.
 *
 * Built on top of `Command` and `Popover`. Supports:
 * - Controlled query value and change handler.
 * - Grouped results with custom item rendering and selection callbacks.
 * - Loading and empty states.
 * - Controlled or uncontrolled popover open state.
 *
 * @template T The item type rendered in the result list.
 * @param inputSearchProps Component props.
 * @returns A search input with a popover of grouped results.
 */
export function CommandSearchInput<T>(inputSearchProps: InputSearchProps<T>) {
  const {
    placeholder = 'Szukaj...',
    searchQuery,
    onSearchQueryChange,
    groups,
    isLoading = false,
    emptyMessage = 'Brak wyników',
    loadingMessage = 'Ładowanie...',
    className,
    openCommand,
    onOpenCommandChange,
  } = inputSearchProps;

  const [isOpenInternal, setIsOpenInternal] = useState<boolean>(false);
  const isOpen: boolean = openCommand !== undefined ? openCommand : isOpenInternal;
  const setIsOpen: (value: boolean) => void = onOpenCommandChange !== undefined ? onOpenCommandChange : setIsOpenInternal;

  const handleSearchChange = (value: string) => {
    onSearchQueryChange(value);
    if (value.length > 0) {
      setIsOpen(true);
    }
  };

  const handleItemSelect = (item: T, group: SearchGroup<T>) => {
    group.onItemSelect(item);
    setIsOpen(false);
    onSearchQueryChange('');
  };

  useEffect(() => {
    if (searchQuery.length === 0) {
      setIsOpen(false);
    }
  }, [searchQuery, setIsOpen]);

  return (
    <div className={className}>
      <Command shouldFilter={false} className="rounded-md">
        <Popover open={isOpen} onOpenChange={setIsOpen}>
          <PopoverTrigger asChild>
            <CommandInput
              value={searchQuery}
              onValueChange={handleSearchChange}
              wrapperClassName="border rounded-md outline-focus-ring focus-within:z-10 outline-0 focus-within:outline-solid focus-within:outline-3 data-[disabled=true]:cursor-not-allowed data-[disabled=true]:opacity-60"
              placeholder={placeholder}
            />
          </PopoverTrigger>
          <PopoverContent
            side="bottom"
            align="start"
            className="min-h-10 w-(--radix-popover-trigger-width) rounded-md p-0 shadow-md"
            onFocus={(e) => e.preventDefault()}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <CommandList>
              <CommandLoading className={isLoading ? '' : 'hidden'}>{loadingMessage}</CommandLoading>

              <CommandEmpty className={isLoading ? 'hidden' : ''}>
                {emptyMessage}. {searchQuery}
              </CommandEmpty>

              {groups.map((group) => {
                const hasGroupItems = group.items.length > 0 && !isLoading;

                if (!hasGroupItems) return null;

                return (
                  <CommandGroup key={group.key} heading={group.label}>
                    {group.items.map((item) => (
                      <CommandItem
                        value={group.getItemKey(item)}
                        key={group.getItemKey(item)}
                        className={group.isItemSelected?.(item) ? 'bg-accent/75' : ''}
                        onSelect={() => handleItemSelect(item, group)}
                      >
                        {group.renderItem(item)}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
            </CommandList>
          </PopoverContent>
        </Popover>
      </Command>
    </div>
  );
}

/**
 * `SearchInput` — a lightweight, debounced text search field for table filtering.
 *
 * Designed primarily for TanStack Table usage:
 * - Debounces user input before calling `setValue` to avoid excessive filtering work.
 * - Provides a clear button and an optional keyboard shortcut hint (⌘K / Ctrl+K).
 * - Exposes a simple API that can be wired to any table/global filter.
 *
 * @param props Component props.
 * @returns A debounced search field wrapped in an `InputGroup`.
 */
export function SearchInput(props: SimpleSearchInputProps) {
  const { placeholder = 'Wyszukaj...', setValue, className } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const isMountedRef = useRef<boolean>(false);
  const lastEmittedValueRef = useRef<string | null>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  const searchDebouncedValue = useDebounceValue(searchValue, 400);
  const { showShortcut, label: shortcutLabel } = usePlatformShortcutLabel({
    macLabel: '⌘K',
    otherLabel: 'Ctrl+K',
  });

  const handleKeyDown = useEffectEvent((event: KeyboardEvent) => {
    if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      event.stopPropagation();
      setSearchValue('');
      inputRef.current?.focus();
    }
  });

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (!isMountedRef.current) {
      return;
    }

    if (lastEmittedValueRef.current === searchDebouncedValue) {
      return;
    }

    // Mark as non-urgent - filtering large datasets is expensive
    startTransition(() => {
      setValue(searchDebouncedValue);
      lastEmittedValueRef.current = searchDebouncedValue;
    });
  }, [searchDebouncedValue, setValue]);

  return (
    <InputGroup className={cn('w-full', className)}>
      <InputGroupInput ref={inputRef} value={searchValue} onChange={(e) => setSearchValue(e.target.value)} placeholder={placeholder} />
      <InputGroupAddon align="inline-start">
        <SearchIcon className="text-muted-foreground" />
      </InputGroupAddon>
      <InputGroupAddon align="inline-end">
        {searchValue ? (
          <InputGroupButton aria-label="Clear input" onClick={() => setSearchValue('')} type="button">
            <CircleXIcon aria-hidden="true" />
          </InputGroupButton>
        ) : (
          showShortcut && <Kbd>{shortcutLabel}</Kbd>
        )}
      </InputGroupAddon>
    </InputGroup>
  );
}
